import type { SelectionRefusal } from '~/domain/workflow/datasetSelection';
import { localization } from '~/i18n';
import { enqueueSnackbar } from '~/utils/notifications';

const MESSAGE_IDS = {
    structureSetWithoutImage: 'structureSet.refused.noImage',
    doseWithoutStructureSet: 'dose.refused.noStructureSet',
    doseWithoutImage: 'dose.refused.noImage',
} as const satisfies Record<SelectionRefusal['reason'], string>;

/** The log text of a structure set refusal: names no structure set. */
const STRUCTURE_SET_REFUSED_LOG_MESSAGE =
    'A structure set was not selected: it is not connected to an image.';

/**
 * Tells the user, as an error, why a dataset was not selected. A structure
 * set is named by its label on screen only; the snackbar's `logMessage`,
 * {@link STRUCTURE_SET_REFUSED_LOG_MESSAGE}, names no set and is what a log
 * of the notification records.
 */
export function notifySelectionRefusal(refusal: SelectionRefusal): void {
    if (refusal.reason === 'structureSetWithoutImage') {
        enqueueSnackbar(
            localization.t(MESSAGE_IDS[refusal.reason], {
                label: refusal.structureSet.structureSetLabel,
            }),
            { variant: 'error', logMessage: STRUCTURE_SET_REFUSED_LOG_MESSAGE }
        );
        return;
    }
    enqueueSnackbar(localization.t(MESSAGE_IDS[refusal.reason]), {
        variant: 'error',
    });
}

/**
 * Tells the user, as a warning, that the structure set just selected cannot
 * be shown because its image is not loaded. Names no structure set.
 */
export function notifyStructureSetNotShown(): void {
    enqueueSnackbar(localization.t('structureSet.notShown.imageNotLoaded'), {
        variant: 'warning',
    });
}
